import { forwardRef } from 'react'
import type { ComponentProps, ReactNode } from 'react'

import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

/* -------------------------------- variants -------------------------------- */

const menuContentVariants = tv({
  base: [
    'bg-surface border-outline-strong shadow-overlay z-50 min-w-[8rem] overflow-hidden rounded-lg border p-1',
    'origin-[var(--transform-origin)]',
    'transition-[opacity,transform] duration-150 ease-out',
    'data-[starting-style]:opacity-0 motion-safe:data-[starting-style]:scale-95',
    'data-[ending-style]:opacity-0 motion-safe:data-[ending-style]:scale-95'
  ]
})

const menuItemVariants = tv({
  base: [
    'relative flex w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none',
    'transition-colors',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
  ],
  variants: {
    variant: {
      default: 'text-surface-title-50 data-[highlighted]:bg-surface-200',
      destructive: 'text-danger-body data-[highlighted]:bg-danger-bg'
    },
    inset: {
      true: 'pl-8'
    }
  },
  defaultVariants: {
    variant: 'default',
    inset: false
  }
})

/* ---------------------------------- types --------------------------------- */

export type DropdownMenuItemVariant = NonNullable<
  VariantProps<typeof menuItemVariants>['variant']
>

export type DropdownMenuContentProps = MenuPrimitive.Popup.Props &
  Pick<
    MenuPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  > & {
    children: ReactNode
  }

export type DropdownMenuItemProps = MenuPrimitive.Item.Props &
  VariantProps<typeof menuItemVariants> & {
    inset?: boolean
  }

export type DropdownMenuCheckboxItemProps = MenuPrimitive.CheckboxItem.Props

export type DropdownMenuRadioItemProps = MenuPrimitive.RadioItem.Props

export type DropdownMenuSubTriggerProps = MenuPrimitive.SubmenuTrigger.Props

export type DropdownMenuLabelProps = MenuPrimitive.GroupLabel.Props

/* ------------------------------- components ------------------------------- */

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot='dropdown-menu' {...props} />
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot='dropdown-menu-trigger' {...props} />
}

function DropdownMenuContent({
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  alignOffset = 0,
  className,
  children,
  ...props
}: DropdownMenuContentProps) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className='isolate z-50'
      >
        <MenuPrimitive.Popup
          data-slot='dropdown-menu-content'
          className={cn(menuContentVariants(), className)}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot='dropdown-menu-group' {...props} />
}

const DropdownMenuLabel = forwardRef<HTMLDivElement, DropdownMenuLabelProps>(
  ({ className, ...props }, ref) => (
    <MenuPrimitive.GroupLabel
      ref={ref}
      data-slot='dropdown-menu-label'
      className={cn(
        'text-surface-body px-2 py-1.5 text-xs font-semibold',
        className
      )}
      {...props}
    />
  )
)
DropdownMenuLabel.displayName = 'DropdownMenuLabel'

const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ variant = 'default', inset = false, className, ...props }, ref) => (
    <MenuPrimitive.Item
      ref={ref}
      data-slot='dropdown-menu-item'
      data-variant={variant}
      className={cn(menuItemVariants({ variant, inset }), className)}
      {...props}
    />
  )
)
DropdownMenuItem.displayName = 'DropdownMenuItem'

function DropdownMenuSeparator({
  className,
  ...props
}: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot='dropdown-menu-separator'
      className={cn('bg-outline -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      data-slot='dropdown-menu-shortcut'
      className={cn(
        'text-surface-body ml-auto text-xs tracking-widest',
        className
      )}
      {...props}
    />
  )
}

const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  DropdownMenuCheckboxItemProps
>(({ className, children, ...props }, ref) => (
  <MenuPrimitive.CheckboxItem
    ref={ref}
    data-slot='dropdown-menu-checkbox-item'
    className={cn(menuItemVariants({ variant: 'default' }), className)}
    {...props}
  >
    <span className='pointer-events-none flex size-3.5 items-center justify-center'>
      <MenuPrimitive.CheckboxItemIndicator>
        <span className='material-symbols-rounded text-[14px]'>check</span>
      </MenuPrimitive.CheckboxItemIndicator>
    </span>
    {children}
  </MenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem'

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot='dropdown-menu-radio-group'
      {...props}
    />
  )
}

const DropdownMenuRadioItem = forwardRef<
  HTMLDivElement,
  DropdownMenuRadioItemProps
>(({ className, children, ...props }, ref) => (
  <MenuPrimitive.RadioItem
    ref={ref}
    data-slot='dropdown-menu-radio-item'
    className={cn(menuItemVariants({ variant: 'default' }), className)}
    {...props}
  >
    <span className='pointer-events-none flex size-3.5 items-center justify-center'>
      <MenuPrimitive.RadioItemIndicator>
        <span className="material-symbols-rounded text-[14px] [font-variation-settings:'FILL'_1]">
          circle
        </span>
      </MenuPrimitive.RadioItemIndicator>
    </span>
    {children}
  </MenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

function DropdownMenuSub({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot='dropdown-menu-sub' {...props} />
}

const DropdownMenuSubTrigger = forwardRef<
  HTMLDivElement,
  DropdownMenuSubTriggerProps
>(({ className, children, ...props }, ref) => (
  <MenuPrimitive.SubmenuTrigger
    ref={ref}
    data-slot='dropdown-menu-sub-trigger'
    className={cn(
      'text-surface-title-50 data-[highlighted]:bg-surface-200 flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none',
      className
    )}
    {...props}
  >
    {children}
    <span className='material-symbols-rounded ml-auto text-[16px]'>
      chevron_right
    </span>
  </MenuPrimitive.SubmenuTrigger>
))
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger'

function DropdownMenuSubContent({
  side = 'right',
  sideOffset = 0,
  align = 'start',
  alignOffset = -3,
  ...props
}: DropdownMenuContentProps) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className='isolate z-50'
      >
        <MenuPrimitive.Popup
          data-slot='dropdown-menu-sub-content'
          className={cn(menuContentVariants(), props.className)}
          {...props}
        >
          {props.children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
}
